import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabaseClient'
import { useAuth } from '@/features/auth/AuthContext'
import { reserveUnit, fetchMyReservations } from '@/features/reservations/api/reservationsApi'
import { getCurrentDiscount } from '@/features/discounts/api/discountsApi'
import ReservationCountdown from '@/components/ReservationCountdown'
import { calculateYield, formatYield } from '@/lib/yieldCalculator'

export default function UnitDetailPage() {
  const { projectId, unitId } = useParams()
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [reservationNotes, setReservationNotes] = useState('')
  const [showReserveModal, setShowReserveModal] = useState(false)

  const { data: unitData, isLoading: unitLoading } = useQuery({
    queryKey: ['unit', unitId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('units')
        .select(`
          *,
          project:projects(
            id,
            name,
            location,
            address,
            status,
            developer:developer_id(
              id,
              full_name,
              company_name,
              email
            )
          ),
          unit_type:unit_types(*)
        `)
        .eq('id', unitId)
        .single()

      if (error) throw error
      return data
    },
  })

  const { data: reservationsData } = useQuery({
    queryKey: ['my-active-reservations'],
    queryFn: () => fetchMyReservations('active'),
    enabled: !!user && profile?.role === 'buyer',
  })

  const reservedUnitIds = new Set(
    reservationsData?.data?.map(r => r.unit?.id).filter(Boolean) || []
  )

  const { data: currentDiscountData } = useQuery({
    queryKey: ['current-discount', projectId],
    queryFn: () => getCurrentDiscount(projectId),
    enabled: !!user && profile?.role === 'buyer',
  })

  const { data: unitEligibilityData } = useQuery({
    queryKey: ['unit-eligibility-single', unitId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('unit_discount_eligibility')
        .select('unit_id, is_eligible')
        .eq('unit_id', unitId)
        .maybeSingle()

      if (error) throw error
      return data?.is_eligible !== false
    },
    enabled: !!user && profile?.role === 'buyer' && !!unitId,
  })

  const currentDiscount = currentDiscountData?.data
  const isEligibleForDiscount = unitEligibilityData

  const reserveMutation = useMutation({
    mutationFn: async ({ unitId, notes }) => {
      const result = await reserveUnit(unitId, notes)
      if (result.error) throw new Error(result.error.message || 'Failed to reserve unit')

      return result.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['my-active-reservations'])
      queryClient.invalidateQueries(['unit', unitId])
      setShowReserveModal(false)
      navigate('/dashboard/buyer')
    },
  })

  const handleReserveClick = () => {
    if (!user) {
      navigate('/signin')
      return
    }

    if (profile?.role !== 'buyer') {
      alert('Only buyers can reserve units')
      return
    }

    setShowReserveModal(true)
  }

  const handleReserveSubmit = () => {
    reserveMutation.mutate({
      unitId: unitData.id,
      notes: reservationNotes,
    })
  }

  if (unitLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner spinner-lg text-primary-600 mb-4"></div>
          <p className="text-secondary-600">Loading unit details...</p>
        </div>
      </div>
    )
  }

  if (!unitData) {
    return (
      <div className="container-custom py-12">
        <div className="alert-error">
          <p>Unit not found</p>
        </div>
        <Link to="/projects" className="btn-primary mt-4">
          Back to Projects
        </Link>
      </div>
    )
  }

  const unit = unitData
  const project = unit.project
  const isReservedByMe = reservedUnitIds.has(unit.id)
  const discountedPrice = currentDiscount && unit.status === 'available' && isEligibleForDiscount
    ? unit.price - (unit.price * parseFloat(currentDiscount.discount_percent)) / 100
    : null

  const rentPerWeek = unit.rent_per_week || unit.unit_type?.rent_per_week
  const effectivePrice = discountedPrice || unit.price
  const discountPercent = currentDiscount && isEligibleForDiscount ? parseFloat(currentDiscount.discount_percent) : 0
  const yieldValue = calculateYield(rentPerWeek, unit.price, discountPercent)

  return (
    <div className="bg-secondary-50 min-h-screen">
      <div className="container-custom py-4 sm:py-8">
        <nav className="mb-4 sm:mb-6 text-sm px-4 sm:px-0">
          <Link to="/projects" className="text-primary-600 hover:text-primary-700">
            Projects
          </Link>
          <span className="mx-2 text-secondary-400">/</span>
          <Link to={`/projects/${project.id}`} className="text-primary-600 hover:text-primary-700">
            {project.name}
          </Link>
          <span className="mx-2 text-secondary-400">/</span>
          <span className="text-secondary-600">Unit {unit.unit_number}</span>
        </nav>

        <div className="max-w-3xl mx-auto">
          <div className="space-y-0 sm:space-y-6">
            <div className="card p-4 sm:p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-secondary-900 mb-2">
                    Unit {unit.unit_number}
                  </h1>
                  <p className="text-lg text-secondary-600">{project.name}</p>
                  <p className="text-sm text-secondary-500 flex items-center mt-1">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {project.location}
                  </p>
                </div>
                <span className={`badge ${
                  unit.status === 'available' ? 'badge-success' :
                  unit.status === 'reserved' ? 'badge-warning' :
                  unit.status === 'sold' ? 'badge-error' :
                  'badge-secondary'
                }`}>
                  {isReservedByMe ? 'Your Reservation' : unit.status}
                </span>
              </div>

              {discountedPrice ? (
                <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-semibold text-green-800">
                      Discount Available: {parseFloat(currentDiscount.discount_percent).toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex items-baseline gap-3">
                    <span className="text-2xl font-bold text-green-700">
                      ${discountedPrice.toLocaleString('en-NZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </span>
                    <span className="text-lg text-secondary-400 line-through">
                      ${unit.price.toLocaleString('en-NZ')}
                    </span>
                  </div>
                  <p className="text-sm text-green-600 mt-1">
                    Save ${((unit.price * parseFloat(currentDiscount.discount_percent)) / 100).toLocaleString('en-NZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </p>
                  {currentDiscount.expires_at && (
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <ReservationCountdown expiresAt={currentDiscount.expires_at} label="Discount expires in" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="mb-6">
                  <div className="text-3xl font-bold text-primary-600">
                    ${unit.price.toLocaleString('en-NZ')}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-secondary-900">Unit Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 20v-8a2 2 0 012-2h14a2 2 0 012 2v8m-18 0h18M5 10V6a2 2 0 012-2h10a2 2 0 012 2v4M7 14h2m4 0h2" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-secondary-500">Bedrooms</p>
                      <p className="font-semibold text-secondary-900">{unit.bedrooms}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 8V6a2 2 0 012-2h8a2 2 0 012 2v2m-4 4h.01M4 16h16a1 1 0 001-1V8H3v7a1 1 0 001 1zm0 0v2a2 2 0 002 2h1m-3-4v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-secondary-500">Bathrooms</p>
                      <p className="font-semibold text-secondary-900">{unit.bathrooms}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-secondary-500">Size</p>
                      <p className="font-semibold text-secondary-900">{unit.sqm} m²</p>
                    </div>
                  </div>

                  {unit.car_parks > 0 && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6a2 2 0 012-2h2a2 2 0 012 2v6m-6 0h6m-6 0H7m12 0h-2M7 17V8a2 2 0 012-2h6a2 2 0 012 2v9M7 17H5m14 0h2" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs text-secondary-500">Car Parks</p>
                        <p className="font-semibold text-secondary-900">{unit.car_parks}</p>
                      </div>
                    </div>
                  )}
                </div>

                {unit.unit_type && (
                  <div className="pt-4 border-t border-secondary-200">
                    <p className="text-sm text-secondary-600">
                      <span className="font-medium">Unit Type:</span> {unit.unit_type.name}
                    </p>
                    {unit.unit_type.description && (
                      <p className="text-sm text-secondary-600 mt-2">{unit.unit_type.description}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Floor Plan */}
            <div className="card p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">Floor Plan</h3>
              {unit.unit_type?.floor_plan_url || unit.floor_plan_url ? (
                <img
                  src={unit.unit_type?.floor_plan_url || unit.floor_plan_url}
                  alt={`Unit ${unit.unit_number} floor plan`}
                  className="w-full rounded-lg border border-secondary-200"
                />
              ) : (
                <div className="w-full h-64 sm:h-96 bg-secondary-100 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <svg className="w-12 sm:w-16 h-12 sm:h-16 text-secondary-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-secondary-600 text-sm sm:text-base">No floor plan available yet</p>
                  </div>
                </div>
              )}
            </div>

            {rentPerWeek && yieldValue && profile?.buyer_intent === 'investment' && (
              <div className="card p-4 sm:p-6 bg-gradient-to-br from-mint-50 to-mint-100 border-2 border-mint-300">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-semibold text-secondary-900">Investment Yield</h3>
                  <div className="w-10 h-10 bg-mint-200 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-mint-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm text-secondary-600">Weekly Rent</span>
                    <span className="text-lg font-semibold text-secondary-900">
                      ${rentPerWeek.toLocaleString('en-NZ')}
                    </span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm text-secondary-600">Annual Income</span>
                    <span className="text-lg font-semibold text-secondary-900">
                      ${(rentPerWeek * 52).toLocaleString('en-NZ')}
                    </span>
                  </div>
                  <div className="flex justify-between items-baseline pt-3 border-t border-mint-300">
                    <span className="text-sm text-secondary-600">Effective Price</span>
                    <span className="text-lg font-semibold text-secondary-900">
                      ${effectivePrice.toLocaleString('en-NZ')}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t-2 border-mint-400">
                    <span className="font-medium text-secondary-900">Gross Yield</span>
                    <span className="text-3xl font-bold text-mint-700">
                      {formatYield(yieldValue)}
                    </span>
                  </div>
                  {discountedPrice && (
                    <div className="pt-3 mt-3 border-t border-mint-300">
                      <p className="text-xs text-mint-800">
                        Yield calculated using discounted price of ${discountedPrice.toLocaleString('en-NZ')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Purchase Process */}
            <div className="card p-4 sm:p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-200 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900 mb-1">How to Purchase This Unit</h3>
                  <p className="text-sm text-secondary-600">Simple steps to secure your new home</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    1
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-900 mb-1">Reserve the Unit</h4>
                    <p className="text-sm text-secondary-700">
                      Click "Reserve This Unit" to secure it for {unit.status === 'available' ? '72 hours' : 'a limited time'}. This gives you time to review details and consider your purchase without pressure.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    2
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-900 mb-1">Review & Consider</h4>
                    <p className="text-sm text-secondary-700">
                      Take your time to review the unit details, visit the property if needed, and arrange financing. The reservation holds the unit exclusively for you.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    3
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-900 mb-1">Sign Sale & Purchase Agreement</h4>
                    <p className="text-sm text-secondary-700">
                      Once you're ready, we'll help you formalize the purchase with a legally binding Sale and Purchase Agreement through our platform.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    4
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-900 mb-1">Settlement & Move In</h4>
                    <p className="text-sm text-secondary-700">
                      Complete the settlement process and receive the keys to your new home!
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-blue-300">
                <p className="text-xs text-blue-900 font-medium">
                  No commitment until you sign the Agreement. Your reservation can be withdrawn at any time before signing.
                </p>
              </div>
            </div>

            {isReservedByMe ? (
              <Link
                to="/dashboard/buyer"
                className="btn-primary w-full text-center text-lg py-4"
              >
                View Your Reservation
              </Link>
            ) : unit.status === 'available' && user && profile?.role === 'buyer' ? (
              <button
                onClick={handleReserveClick}
                className="btn-primary w-full text-lg py-4"
              >
                Reserve This Unit
              </button>
            ) : unit.status === 'available' && !user ? (
              <Link
                to="/signin"
                className="btn-primary w-full text-center text-lg py-4"
              >
                Sign In to Reserve
              </Link>
            ) : (
              <button
                disabled
                className="btn-outline w-full text-lg py-4 opacity-50 cursor-not-allowed"
              >
                {unit.status === 'reserved' ? 'Reserved' : 'Not Available'}
              </button>
            )}
          </div>
        </div>
      </div>

      {showReserveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="card max-w-lg w-full p-4 sm:p-6 my-8">
            <h3 className="text-xl font-bold text-secondary-900 mb-4">
              Reserve Unit {unit.unit_number}
            </h3>

            {/* What Happens Next */}
            <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-8 h-8 bg-blue-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">What happens after you reserve?</h4>
                  <ol className="text-sm text-blue-800 space-y-1.5 list-decimal list-inside">
                    <li>This unit is held exclusively for you for 72 hours</li>
                    <li>Use this time to review details and arrange financing</li>
                    <li>When ready, proceed to formalize a Sale and Purchase Agreement</li>
                    <li>No commitment until you sign the Agreement</li>
                  </ol>
                </div>
              </div>
            </div>

            {currentDiscount && isEligibleForDiscount && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-semibold text-green-800">
                    Discount Will Be Applied
                  </span>
                </div>
                <p className="text-sm text-green-700">
                  Your {parseFloat(currentDiscount.discount_percent).toFixed(1)}% discount will be automatically locked when you reserve. You can allocate it after reservation from your dashboard.
                </p>
              </div>
            )}

            <div className="mb-4">
              <label className="label">Notes (optional)</label>
              <textarea
                value={reservationNotes}
                onChange={(e) => setReservationNotes(e.target.value)}
                className="input min-h-[100px]"
                placeholder="Add any notes or special requests..."
              />
            </div>

            {reserveMutation.error && (
              <div className="alert-error mb-4">
                {reserveMutation.error.message}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowReserveModal(false)}
                className="btn-outline flex-1"
                disabled={reserveMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={handleReserveSubmit}
                className="btn-primary flex-1"
                disabled={reserveMutation.isPending}
              >
                {reserveMutation.isPending ? 'Reserving...' : 'Confirm Reservation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
